import { ErrorHandler, Injectable } from '@angular/core';

/**
 * Recovers from stale-deploy chunk failures. After a new build ships, a client that
 * still has the previous bundle open requests hashed lazy chunks that no longer exist;
 * the SPA fallback (`/*  /index.html  200`) returns index.html with a text/html MIME
 * type, so the module load fails with a ChunkLoadError. A single full reload pulls the
 * fresh index.html (and its new chunk hashes) and the navigation succeeds.
 *
 * A short-lived sessionStorage timestamp guards against a reload loop if the chunk is
 * genuinely unreachable; after the window expires the recovery re-arms itself, so a
 * later, unrelated chunk error is still recovered.
 */
@Injectable()
export class ChunkErrorHandler extends ErrorHandler {
    private static readonly RELOAD_KEY = 'sai-chunk-reload-at';
    private static readonly RELOAD_WINDOW_MS = 10_000;

    override handleError(error: unknown): void {
        if (this.isChunkLoadError(error) && this.reloadOnce()) {
            return;
        }
        super.handleError(error);
    }

    private isChunkLoadError(error: unknown): boolean {
        const message =
            (error as { message?: string })?.message ?? String(error);
        return /ChunkLoadError|Loading chunk [^ ]+ failed|Failed to load module script/i.test(
            message
        );
    }

    /** Reloads at most once per window; returns true when a reload was triggered. */
    private reloadOnce(): boolean {
        try {
            const last = Number(
                sessionStorage.getItem(ChunkErrorHandler.RELOAD_KEY) ?? 0
            );
            if (Date.now() - last < ChunkErrorHandler.RELOAD_WINDOW_MS) {
                return false;
            }
            sessionStorage.setItem(
                ChunkErrorHandler.RELOAD_KEY,
                String(Date.now())
            );
            location.reload();
            return true;
        } catch {
            return false;
        }
    }
}
